import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import './premiumThemes.css';

export type ThemeName = 'light' | 'dark' | 'midnight' | 'royal' | 'emerald' | 'rose';

export const PREMIUM_THEMES: ThemeName[] = ['midnight', 'royal', 'emerald', 'rose'];
export const FREE_THEMES: ThemeName[] = ['light', 'dark'];
export const ALL_THEMES: ThemeName[] = [...FREE_THEMES, ...PREMIUM_THEMES];
export const THEME_LABEL_KEYS: Record<ThemeName, string> = {
  light: 'nameLight',
  dark: 'nameDark',
  midnight: 'nameMidnight',
  royal: 'nameRoyal',
  emerald: 'nameEmerald',
  rose: 'nameRose',
};

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
  isPremiumTheme: (t: ThemeName) => boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  darkMode: true,
  toggleDarkMode: () => {},
  theme: 'dark',
  setTheme: () => {},
  isPremiumTheme: () => false,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const stored = localStorage.getItem('theme') as ThemeName | null;
    if (stored && ALL_THEMES.includes(stored)) return stored;
    const oldDark = localStorage.getItem('darkMode');
    return oldDark === 'false' ? 'light' : 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    // Geriye dönük uyumluluk
    const isDark = theme !== 'light' && theme !== 'rose';
    localStorage.setItem('darkMode', String(isDark));
  }, [theme]);

  const setTheme = useCallback((t: ThemeName) => setThemeState(t), []);
  const toggleDarkMode = useCallback(() => {
    setThemeState(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const isPremiumTheme = useCallback((t: ThemeName) => PREMIUM_THEMES.includes(t), []);
  const darkMode = theme !== 'light' && theme !== 'rose';

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, theme, setTheme, isPremiumTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
